import { z } from 'zod';
import { StoredPerfConfigSchema } from './perf-config.js';
import { PerfArmSchema } from './perf-result.js';

/** Config staged for one guest arm by the perf job. */
export const RunnerConfigSchema = StoredPerfConfigSchema.safeExtend({
  /** HTTPS URL for the claimed sandbox's public app port. */
  baseUrl: z.url({ protocol: /^https$/ }),
  otelExpected: z.boolean(),
});

export type RunnerConfig = z.infer<typeof RunnerConfigSchema>;

const LighthousePageRoundSchema = z.strictObject({
  name: z.string().min(1),
  lcpMs: z.number().nonnegative(),
  tbtMs: z.number().nonnegative(),
  fcpMs: z.number().nonnegative(),
  cls: z.number().nonnegative(),
  speedIndexMs: z.number().nonnegative(),
  bytes: z.number().nonnegative(),
  finalUrl: z.string().min(1),
  documentStatus: z.number().int(),
});

const K6RoundSchema = z.strictObject({
  p50Ms: z.number().nonnegative(),
  p95Ms: z.number().nonnegative(),
  requestsPerSecond: z.number().nonnegative(),
  errorRate: z.number().nonnegative(),
});

const OtelEndpointRoundSchema = z.strictObject({
  endpoint: z.string().min(1),
  count: z.number().int().nonnegative(),
  p50Ms: z.number().nonnegative(),
  p95Ms: z.number().nonnegative(),
});

const PerfRoundSlowSpanSchema = z.strictObject({
  name: z.string().min(1),
  count: z.number().int().nonnegative(),
  p95Ms: z.number().nonnegative(),
  totalMs: z.number().nonnegative(),
});

/** One runner JSONL record for a measured round on one arm. */
export const RoundSummarySchema = z.strictObject({
  round: z.number().int().positive(),
  arm: PerfArmSchema,
  lighthouse: z.array(LighthousePageRoundSchema).optional(),
  k6: K6RoundSchema.optional(),
  otel: z
    .strictObject({
      endpoints: z.array(OtelEndpointRoundSchema),
      topSlowSpans: z.array(PerfRoundSlowSpanSchema),
    })
    .optional(),
});

export type RoundSummary = z.infer<typeof RoundSummarySchema>;
